import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { IDT } from '@seen/core/models/care-teams/idt'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import {
  type ExpectedOwnerLookups,
  type ExpectedOwnerResolution,
  resolveExpectedOwner,
} from '@seen/core/queued-edits/expected-owner'
import type { ResourceChangeSet } from '@seen/core/queued-edits/model'
import { resolveId } from '@seen/core/references'
import { Role } from '@seen/core/roles'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export function resolveExpectedOwnerWhenReady(
  resolution: ExpectedOwnerResolution | undefined,
  lookups: ExpectedOwnerLookups,
  candidatePending: boolean
): Reference<Practitioner> | undefined {
  if (candidatePending && resolution?.kind === 'pcp' && resolution.candidate) {
    return undefined
  }
  return resolveExpectedOwner(resolution, lookups)
}

// ##ExpectedOwnerResolution Resolve every change's expected owner in one pass,
// so the review surface can filter by owner as well as render per-card chips.
// Direct ('practitioner'-kind) resolutions need no lookups; 'pcp'-kind ones
// batch two query sets — the candidate requesters (internal-PCP check) and
// each participant's IDT (assigned PCP) — deduplicated across changes. Keyed
// by ResourceChangeSet.id; an unresolved/in-flight owner is simply absent.
export function useExpectedOwners(
  changes: ResourceChangeSet[]
): Record<string, Reference<Practitioner> | undefined> {
  const medplum = useMedplum()
  const resolutions = useMemo(
    () => new Map(changes.map((change) => [change.id, change.expectedOwner])),
    [changes]
  )

  const candidateIds = useMemo(
    () => [
      ...new Set(
        changes.flatMap((c) => {
          const resolution = resolutions.get(c.id)
          const candidate =
            resolution?.kind === 'pcp' ? resolution.candidate : undefined
          const id = candidate ? resolveId(candidate) : undefined
          return id ? [id] : []
        })
      ),
    ],
    [changes, resolutions]
  )
  const candidateQueries = useQueries({
    queries: candidateIds.map((id) => ({
      queryKey: cacheKeys.read('Practitioner', id),
      queryFn: async () => medplum.readResource('Practitioner', id),
    })),
  })

  const patientRefs = useMemo(
    () => [
      ...new Set(
        changes.flatMap((c) =>
          resolutions.get(c.id)?.kind === 'pcp' && c.patientReference
            ? [c.patientReference]
            : []
        )
      ),
    ],
    [changes, resolutions]
  )
  const idtQueries = useQueries({
    queries: patientRefs.map((reference) => ({
      queryKey: ['queued-edits-idt-pcp', reference],
      queryFn: async () =>
        medplum.searchOneResourceClass(IDT, IDT.byParticipant({ reference })),
    })),
  })

  const candidateKey = candidateQueries
    .map((q) => `${q.dataUpdatedAt}:${q.status}`)
    .join(',')
  const idtKey = idtQueries.map((q) => q.dataUpdatedAt).join(',')

  // candidateKey/idtKey stand in for the useQueries result arrays (fresh
  // identity every render) and capture their per-query data changes.
  // candidateKey/idtKey are content hashes of the useQueries results
  return useMemo(() => {
    const candidateById = new Map(
      candidateIds.map((id, index) => {
        const data = candidateQueries[index]?.data
        return [
          id,
          data ? ExtendedPractitioner.fromFhirResource(data) : undefined,
        ] as const
      })
    )
    const idtPcpByPatient = new Map(
      patientRefs.map((reference, index) => {
        const idt = idtQueries[index]?.data
        return [
          reference,
          idt?.getMemberByRole(Role.PCP)?.practitioner,
        ] as const
      })
    )
    return Object.fromEntries(
      changes.map((change) => {
        const resolution = resolutions.get(change.id)
        const candidate =
          resolution?.kind === 'pcp' ? resolution.candidate : undefined
        const candidateId = candidate ? resolveId(candidate) : undefined
        const candidateIndex = candidateId
          ? candidateIds.indexOf(candidateId)
          : -1
        return [
          change.id,
          resolveExpectedOwnerWhenReady(
            resolution,
            {
              candidate: candidateId
                ? candidateById.get(candidateId)
                : undefined,
              idtPcp: change.patientReference
                ? idtPcpByPatient.get(change.patientReference)
                : undefined,
            },
            candidateIndex >= 0 &&
              Boolean(candidateQueries[candidateIndex]?.isPending)
          ),
        ]
      })
    )
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [changes, resolutions, candidateIds, patientRefs, candidateKey, idtKey])
}
